// src/components/pdf-viewer/PDFViewer.tsx
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Spin, Alert, message } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import {
  PdfLoader,
  PdfHighlighter,
  Highlight,
  Popup,
  AreaHighlight,
} from 'react-pdf-highlighter';
import type { IHighlight, NewHighlight, ScaledPosition, Content, Comment } from 'react-pdf-highlighter';
import { PDFToolbar } from './PDFToolbar';
import { AnnotationSidebar } from './AnnotationSidebar';
import { ReportAssistant, ChatMessage } from './ReportAssistant';
import { AnnotationTooltip } from './AnnotationTooltip';
import {
  NarrationPlayer,
  NarrationCaptions,
  type NarrationSection,
  type NarrationPlayerHandle,
} from './NarrationPlayer';
import { PDFSearchBox } from './PDFSearchBox';
import { exportAndDownloadPage } from '../../utils/pdfExport';
import type {
  Annotation,
  CreateAnnotationRequest,
  UpdateAnnotationRequest,
} from '../../types/annotation';
import { ANNOTATION_COLORS } from '../../types/annotation';

// Import the CSS for react-pdf-highlighter
import 'react-pdf-highlighter/dist/esm/style/PdfHighlighter.css';
import 'react-pdf-highlighter/dist/esm/style/Highlight.css';
import 'react-pdf-highlighter/dist/esm/style/AreaHighlight.css';

// Override PDF.js's default search-match colors. Defaults are magenta for
// all matches and dark green for the active match — both render very dark
// under the textLayer's `mix-blend-mode: multiply`. Use a soft yellow for
// all hits and an amber for the active match (highlighter-pen look).
const searchHighlightStyles = `
  .textLayer .highlight {
    background-color: rgba(255, 222, 23, 0.55) !important;
    border-radius: 3px !important;
  }
  .textLayer .highlight.selected {
    background-color: rgba(255, 145, 0, 0.7) !important;
  }
`;

interface PDFViewerProps {
  pdfUrl: string;
  documentId: string;
  /** Title shown in the toolbar's top row (left side). */
  documentTitle?: string;
  /** When provided, renders a close (×) button in the toolbar's top-right.
   *  Used by PDFViewerModal to dismiss the full-screen modal. */
  onClose?: () => void;
  annotations: Annotation[];
  onAnnotationCreate: (annotation: CreateAnnotationRequest) => Promise<void>;
  onAnnotationUpdate: (id: string, data: UpdateAnnotationRequest) => Promise<void>;
  onAnnotationDelete: (id: string) => Promise<void>;
  hideExport?: boolean;
  exportPdfUrl?: string;
  onExportPdf?: () => void;
  onGenerateEstimate?: () => void;
  estimateLoading?: boolean;
  enableWorkOrder?: boolean;
  onGenerateWorkOrder?: (annotationIds: string[]) => Promise<Blob | null>;
  workOrderLoading?: boolean;
  /** When provided, shows an "Ask AI" toggle that opens a chat panel. The
   *  owner performs the (authed) streaming fetch and returns the Response. */
  enableAssistant?: boolean;
  onAssistantSend?: (messages: ChatMessage[]) => Promise<Response>;
  /** Optional narration sections — when provided, renders an in-page audio
   *  player at the bottom of the viewer with captions and page-sync. */
  sections?: NarrationSection[];
}

// Generate a unique ID
const getNextId = () => String(Math.random()).slice(2);

// Mobile detection helper
const isMobileDevice = (): boolean => {
  return window.innerWidth < 768;
};

// Convert our Annotation type to the library's IHighlight type
const annotationToHighlight = (annotation: Annotation): IHighlight => {
  // For text highlights, put text in content.text
  // For area highlights, leave content.text empty so the library renders it as area
  const isTextType = annotation.type === 'text';

  return {
    id: annotation.id,
    position: annotation.position as ScaledPosition,
    content: {
      text: isTextType ? (annotation.comment || ' ') : '', // Text highlights need content
    },
    comment: {
      text: annotation.comment || '',
      emoji: '',
    },
  };
};

export const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfUrl,
  documentId,
  documentTitle,
  onClose,
  annotations,
  onAnnotationCreate,
  onAnnotationUpdate,
  onAnnotationDelete,
  hideExport = false,
  exportPdfUrl,
  onExportPdf,
  onGenerateEstimate,
  estimateLoading = false,
  enableWorkOrder,
  onGenerateWorkOrder,
  workOrderLoading,
  enableAssistant,
  onAssistantSend,
  sections,
}) => {
  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  // Desktop opens at fit-height (whole page visible); mobile starts at
  // fit-width (page fills the screen width). Numeric strings are absolute
  // zoom levels.
  const [pdfScaleValue, setPdfScaleValue] = useState<string>(
    isMobileDevice() ? 'page-width' : 'page-fit',
  );
  // Actual rendered scale read back from PDF.js — used by the toolbar to
  // show the real percentage even when in fit-width / fit-height mode.
  const [effectiveScale, setEffectiveScale] = useState<number>(1);
  // PDF.js spread modes:
  //   0 = NONE   (single page)
  //   1 = ODD    pairs grouped as (1,2)(3,4)(5,6)(7,8)…  — odd pages on LEFT
  //   2 = EVEN   pairs grouped as (1)(2,3)(4,5)(6,7)…    — even pages on LEFT
  //                                                       (page 1 alone)
  // We pick this dynamically per-section during narration so the section's
  // first page lands on the LEFT of a spread alongside its content page on
  // the right. The user-facing toggle is intentionally hidden — spread
  // layout is an internal narration feature.
  const [spreadMode, setSpreadMode] = useState<0 | 1 | 2>(0);
  // Sidebar is a slide-in overlay (not a flex column). On desktop it starts
  // open so the annotations are immediately visible alongside the report;
  // on mobile it stays closed (would cover the whole screen at 100% width)
  // until the user taps the Comments button.
  const [showSidebar, setShowSidebar] = useState(!isMobileDevice());
  const [showAssistant, setShowAssistant] = useState(false);
  // One-shot onboarding nudge toward the AI assistant, shown centered on load.
  const [showAiHint, setShowAiHint] = useState(false);

  // Compute display zoom for toolbar:
  //   -1 => page-width  |  -2 => page-fit (fit height)  |  numeric => zoom
  const displayZoom =
    pdfScaleValue === 'page-width'
      ? -1
      : pdfScaleValue === 'page-fit'
      ? -2
      : parseFloat(pdfScaleValue) || 1.0;
  const [exportLoading, setExportLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileDevice());

  // Tooltip state for creating/editing annotations
  const [tooltipState, setTooltipState] = useState<{
    visible: boolean;
    mode: 'create' | 'edit';
    position: { top: number; left: number };
    annotation?: Annotation;
    tempHighlight?: NewHighlight;
  } | null>(null);

  // Ref to PDF document
  const pdfDocumentRef = useRef<any>(null);
  // Latest scale value, read by the "apply on ready" effect without re-subscribing.
  const pdfScaleValueRef = useRef<string>('page-width');
  const scrollViewerToRef = useRef<((highlight: IHighlight) => void) | null>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const hideTipAndSelectionRef = useRef<(() => void) | null>(null);
  const savedScrollTopRef = useRef<number | null>(null);
  const prevAnnotationsRef = useRef(annotations);

  // Narration player state
  const narrationPlayerRef = useRef<NarrationPlayerHandle | null>(null);
  const programmaticScrollUntilRef = useRef<number>(0); // timestamp; ignore scroll events before this
  const [captionText, setCaptionText] = useState('');
  const [captionVisible, setCaptionVisible] = useState(false);
  const [isNarrating, setIsNarrating] = useState(false);
  const hasAutoFitHeightRef = useRef(false);
  // True when the user has started narration at least once. Used to decide
  // whether to show the "click play to continue" bubble after an auto-pause.
  const hasStartedNarrationRef = useRef(false);
  const [showResumeHint, setShowResumeHint] = useState(false);

  // Pauses narration and shows the resume hint bubble. Called by user-driven
  // interactions (zoom, opening annotation tooltip).
  const pauseForInteraction = useCallback(() => {
    if (!hasStartedNarrationRef.current) return;
    narrationPlayerRef.current?.pause();
    setShowResumeHint(true);
  }, []);

  // Save scroll position before annotations change (triggered by parent re-render)
  // This runs during render, before the DOM updates
  if (annotations !== prevAnnotationsRef.current) {
    if (viewerContainerRef.current) {
      savedScrollTopRef.current = viewerContainerRef.current.scrollTop;
    }
    prevAnnotationsRef.current = annotations;
  }

  // Convert annotations to highlights for the library (filter out general notes - they have no position)
  const highlights: IHighlight[] = useMemo(() =>
    annotations.filter(a => a.type !== 'general').map(annotationToHighlight),
    [annotations]
  );

  // Restore scroll position after highlights change causes PdfHighlighter to re-render
  useEffect(() => {
    if (savedScrollTopRef.current !== null) {
      const scrollTop = savedScrollTopRef.current;
      savedScrollTopRef.current = null;
      // Use requestAnimationFrame to wait for DOM update after PdfHighlighter re-renders
      requestAnimationFrame(() => {
        if (viewerContainerRef.current) {
          viewerContainerRef.current.scrollTop = scrollTop;
        }
      });
    }
  }, [highlights]);


  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = isMobileDevice();
      setIsMobile(mobile);
      if (mobile && showSidebar) {
        setShowSidebar(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showSidebar]);

  // Keep the ref in sync so the "apply on ready" effect can read the current value.
  useEffect(() => {
    pdfScaleValueRef.current = pdfScaleValue;
  }, [pdfScaleValue]);

  // On the user's FIRST time opening the viewer, briefly show a nudge toward
  // the AI assistant, then fade it out after 4s. Shown once ever (localStorage).
  useEffect(() => {
    if (!(enableAssistant && onAssistantSend)) return;
    try {
      if (localStorage.getItem('spectreviews_ai_hint_seen')) return;
      localStorage.setItem('spectreviews_ai_hint_seen', '1');
    } catch {
      // localStorage unavailable (e.g. private mode) — just show it this once
    }
    const show = window.setTimeout(() => setShowAiHint(true), 500);
    const hide = window.setTimeout(() => setShowAiHint(false), 4500);
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(hide);
    };
  }, [enableAssistant, onAssistantSend]);

  // Apply the initial scale once the PDF.js viewer is actually ready. The scale
  // effect below no-ops at mount because the viewer isn't created yet, so the
  // default (fit-width on mobile) never got applied. 'pagesinit' fires when the
  // viewer has laid out pages and can accept a scale value.
  useEffect(() => {
    const eventBus = (window as any).PdfViewer?.eventBus;
    const applyInitialScale = () => {
      const viewer = (window as any).PdfViewer?.viewer;
      if (viewer) {
        try {
          viewer.currentScaleValue = pdfScaleValueRef.current;
        } catch {
          /* viewer not ready yet */
        }
      }
    };
    applyInitialScale(); // in case pages are already initialized
    if (eventBus) {
      eventBus.on('pagesinit', applyInitialScale);
      return () => {
        try {
          eventBus.off('pagesinit', applyInitialScale);
        } catch {
          /* eventBus may already be torn down */
        }
      };
    }
  }, [totalPages]);

  // Update PDF viewer scale when pdfScaleValue changes
  // The library exposes the viewer on window.PdfViewer for debugging
  useEffect(() => {
    const pdfViewer = (window as any).PdfViewer;
    if (pdfViewer?.viewer) {
      pdfViewer.viewer.currentScaleValue = pdfScaleValue;
    }
    // Read the actual scale PDF.js settled on (so the toolbar can show a
    // concrete percentage instead of "Fit W" / "Fit H").
    const id = window.setTimeout(() => {
      const cs = (window as any).PdfViewer?.viewer?.currentScale;
      if (cs && Number.isFinite(cs)) setEffectiveScale(cs);
    }, 50);
    return () => window.clearTimeout(id);
  }, [pdfScaleValue]);

  // Subscribe to PDF.js's `scalechanging` event so the toolbar's percentage
  // stays in sync with any internal scale changes (resize, sidebar slide).
  useEffect(() => {
    const pdfViewer = (window as any).PdfViewer;
    const eventBus = pdfViewer?.eventBus;
    if (!eventBus) return;
    const handler = (evt: any) => {
      if (evt?.scale && Number.isFinite(evt.scale)) {
        setEffectiveScale(evt.scale);
      }
    };
    eventBus.on('scalechanging', handler);
    return () => {
      try {
        eventBus.off('scalechanging', handler);
      } catch {
        /* eventBus may already be torn down */
      }
    };
  }, [totalPages]);

  // When the sidebar opens/closes on desktop the viewer's available width
  // changes; re-apply the scale value (only for fit-mode values) after the
  // 250ms slide transition so PDF.js recomputes against the new width.
  useEffect(() => {
    if (isMobile) return;
    if (pdfScaleValue !== 'page-width' && pdfScaleValue !== 'page-fit') return;
    const id = window.setTimeout(() => {
      const pdfViewer = (window as any).PdfViewer;
      if (pdfViewer?.viewer) {
        pdfViewer.viewer.currentScaleValue = pdfScaleValue;
      }
    }, 280);
    return () => window.clearTimeout(id);
  }, [showSidebar, isMobile, pdfScaleValue]);

  // Push spread mode into the underlying PDFViewer. Pair any spread mode
  // (ODD or EVEN) with `scrollMode = 3` (PAGE) so exactly one spread fills
  // the viewport — `page-fit` alone calculates against a single page and
  // lets the next spread peek through. Single-page mode uses the default
  // vertical scroll (0).
  useEffect(() => {
    const pdfViewer = (window as any).PdfViewer;
    if (pdfViewer?.viewer) {
      try {
        pdfViewer.viewer.spreadMode = spreadMode;
        pdfViewer.viewer.scrollMode = spreadMode === 0 ? 0 : 3;
      } catch (err) {
        console.warn('[PDFViewer] failed to set spreadMode/scrollMode', err);
      }
    }
  }, [spreadMode]);


  // Handle page navigation. Use PDF.js's native `currentPageNumber` setter
  // (which works in all scroll modes incl. PAGE-snap used in spread mode)
  // and fall back to DOM scrollIntoView only if the viewer isn't ready yet.
  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      // Mark next ~800ms of scroll events as programmatic so the narration
      // player isn't paused by the smooth-scroll it triggered itself.
      programmaticScrollUntilRef.current = Date.now() + 800;

      const pdfViewer = (window as any).PdfViewer;
      const viewer = pdfViewer?.viewer;
      if (viewer) {
        try {
          viewer.currentPageNumber = page;
          return;
        } catch (err) {
          console.warn('[PDFViewer] currentPageNumber set failed', err);
        }
      }

      setTimeout(() => {
        const container = viewerContainerRef.current;
        if (container) {
          const pageElement = container.querySelector(`[data-page-number="${page}"]`);
          if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 50);
    }
  }, [totalPages]);

  // Pause-on-user-scroll: any scroll on the viewer container that is NOT
  // within the programmatic-scroll window pauses narration playback.
  useEffect(() => {
    const container = viewerContainerRef.current;
    if (!container) return;
    if (!sections || sections.length === 0) return;

    const onScroll = () => {
      if (Date.now() < programmaticScrollUntilRef.current) return;
      pauseForInteraction();
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [sections, pauseForInteraction]);

  // Receive caption updates from the narration player. `playing` reflects
  // audio playback; `captionsVisible` reflects whether the CC overlay should
  // show — separating these so toggling CC doesn't re-trigger narration
  // side-effects (auto-fit, page jump).
  const handleCaptionUpdate = useCallback((
    text: string,
    playing: boolean,
    captionsVisible: boolean,
  ) => {
    setCaptionText(text);
    setCaptionVisible(captionsVisible);
    setIsNarrating(playing);
    if (playing) {
      hasStartedNarrationRef.current = true;
      setShowResumeHint(false);
      // First time the user hits Play: jump to page 1 and fit-height so the
      // whole first page is in view. After that, page transitions follow
      // section boundaries via `handleNarrationPageChange`.
      if (!hasAutoFitHeightRef.current) {
        hasAutoFitHeightRef.current = true;
        setPdfScaleValue('page-fit');
        handlePageChange(1);
      }
    }
  }, [handlePageChange]);

  // Pages currently visible in the spread, given an active spread mode and
  // a left-page anchor. Mirrors PDF.js's pairing rules:
  //   ODD  pairs:  (1,2)(3,4)(5,6)(7,8)…   — odd pages on LEFT
  //   EVEN pairs:  (1)(2,3)(4,5)(6,7)…     — even pages on LEFT (1 alone)
  const getVisiblePages = useCallback((page: number, mode: 0 | 1 | 2): Set<number> => {
    if (mode === 0) return new Set([page]);
    if (mode === 1) {
      return page % 2 === 1
        ? new Set([page, page + 1])
        : new Set([page - 1, page]);
    }
    if (page === 1) return new Set([1]);
    return page % 2 === 0
      ? new Set([page, page + 1])
      : new Set([page - 1, page]);
  }, []);

  // Fires when the player crosses a section boundary (NOT on initial mount).
  // If the section's first page is already on the visible spread, leave the
  // layout alone (don't yank the user sideways). Otherwise jump to that page
  // and pick the spread mode that lands it on the LEFT of the spread, so the
  // next page is visible alongside on the right.
  //
  // Only the section's FIRST page (from the script's `[Section:Page]` tag)
  // drives navigation — `pages[1+]` are sibling image pages and aren't a
  // reliable nav anchor.
  const handleNarrationPageChange = useCallback((page: number) => {
    if (isMobile) {
      // Phone: normal vertical scroll (no book view) zoomed so two pages
      // fit in the viewport. Apply spread/scroll/zoom first, THEN navigate
      // — switching modes can reset scroll position, so navigation must be
      // the last imperative call.
      const MOBILE_ZOOM = 0.35;
      const pdfViewer = (window as any).PdfViewer;
      const viewer = pdfViewer?.viewer;
      setSpreadMode(0);
      setPdfScaleValue(String(MOBILE_ZOOM));
      setCurrentPage(page);
      programmaticScrollUntilRef.current = Date.now() + 800;
      if (viewer) {
        try {
          viewer.spreadMode = 0;
          viewer.scrollMode = 0;
          viewer.currentScale = MOBILE_ZOOM;
          viewer.currentPageNumber = page;
        } catch (err) {
          console.warn('[PDFViewer] mobile narration nav failed', err);
        }
      }
      return;
    }
    const visible = getVisiblePages(currentPage, spreadMode);
    if (visible.has(page)) {
      setPdfScaleValue('page-fit');
      return;
    }
    handlePageChange(page);
    setPdfScaleValue('page-fit');
    if (page > 1) {
      // To land `page` on the LEFT of a spread:
      //   • odd page  (3,5,7…) → ODD  pairs: (3,4)(5,6)(7,8)…
      //   • even page (2,4,6…) → EVEN pairs: (2,3)(4,5)(6,7)…
      setSpreadMode(page % 2 === 0 ? 2 : 1);
    } else {
      setSpreadMode(0);
    }
  }, [getVisiblePages, handlePageChange, currentPage, spreadMode, isMobile]);

  // Handle zoom change
  //   -1 => fit to width (page-width)
  //   -2 => fit to height (page-fit)
  //   else => clamped numeric zoom
  const handleZoomChange = useCallback((newZoom: number) => {
    pauseForInteraction();
    if (newZoom === -1) {
      setPdfScaleValue('page-width');
    } else if (newZoom === -2) {
      setPdfScaleValue('page-fit');
    } else {
      const clampedZoom = Math.max(0.25, Math.min(3, newZoom));
      setPdfScaleValue(clampedZoom.toString());
    }
  }, [pauseForInteraction]);

  // Handle page export
  const handleExportPage = useCallback(async () => {
    if (!pdfDocumentRef.current) {
      message.error('PDF not loaded');
      return;
    }

    setExportLoading(true);
    try {
      const pageAnnotations = annotations.filter(a => a.page === currentPage);
      await exportAndDownloadPage(
        pdfDocumentRef.current,
        currentPage,
        pageAnnotations,
        2.0
      );
      message.success('Page exported successfully');
    } catch (error) {
      console.error('Error exporting page:', error);
      message.error('Failed to export page');
    } finally {
      setExportLoading(false);
    }
  }, [currentPage, annotations]);

  // Handle annotation selection from sidebar
  const handleSelectAnnotation = useCallback((annotation: Annotation) => {
    // Navigate to the page
    setCurrentPage(annotation.page);

    // Scroll to the highlight if possible
    if (scrollViewerToRef.current) {
      const highlight = annotationToHighlight(annotation);
      scrollViewerToRef.current(highlight);
    }
  }, []);

  // Handle annotation edit from sidebar
  const handleEditAnnotation = useCallback((annotation: Annotation) => {
    setTooltipState({
      visible: true,
      mode: 'edit',
      position: { top: 100, left: 100 },
      annotation,
    });
  }, []);

  // Handle annotation delete from sidebar
  const handleDeleteAnnotation = useCallback(async (annotationId: string) => {
    try {
      await onAnnotationDelete(annotationId);
    } catch (error) {
      console.error('Error deleting annotation:', error);
    }
  }, [onAnnotationDelete]);

  // Handle adding a general note (document-wide comment, no position)
  const handleAddGeneralNote = useCallback(() => {
    pauseForInteraction();
    setTooltipState({
      visible: true,
      mode: 'create',
      position: { top: 150, left: 150 },
      // No tempHighlight - will be handled specially in handleTooltipSave
    });
  }, [pauseForInteraction]);

  // Handle tooltip save (create or update)
  const handleTooltipSave = useCallback(async (data: { comment: string; tags: string[]; color: string }) => {
    if (!tooltipState) return;

    if (tooltipState.mode === 'create' && !tooltipState.tempHighlight) {
      // General note - no position, page 0
      const newAnnotation: CreateAnnotationRequest = {
        documentId,
        page: 0,
        type: 'general',
        position: null,
        comment: data.comment,
        tags: data.tags,
        color: data.color,
      };

      try {
        await onAnnotationCreate(newAnnotation);
      } catch (error) {
        console.error('Error creating general note:', error);
      }
    } else if (tooltipState.mode === 'create' && tooltipState.tempHighlight) {
      // Create new annotation
      const position = tooltipState.tempHighlight.position;
      const annotationType = position.rects && position.rects.length > 0 ? 'text' : 'area';

      const newAnnotation: CreateAnnotationRequest = {
        documentId,
        page: position.pageNumber || currentPage,
        type: annotationType,
        position: {
          boundingRect: {
            ...position.boundingRect,
            pageNumber: position.pageNumber || currentPage,
          },
          rects: (position.rects || []).map(r => ({
            ...r,
            pageNumber: position.pageNumber || currentPage,
          })),
          pageNumber: position.pageNumber || currentPage,
        },
        comment: data.comment,
        tags: data.tags,
        color: data.color,
      };

      try {
        await onAnnotationCreate(newAnnotation);
      } catch (error) {
        console.error('Error creating annotation:', error);
      }
    } else if (tooltipState.mode === 'edit' && tooltipState.annotation) {
      // Update existing annotation
      try {
        await onAnnotationUpdate(tooltipState.annotation.id, {
          comment: data.comment,
          tags: data.tags,
          color: data.color,
        });
      } catch (error) {
        console.error('Error updating annotation:', error);
      }
    }

    setTooltipState(null);

    // Clean up the selection highlight
    if (hideTipAndSelectionRef.current) {
      hideTipAndSelectionRef.current();
      hideTipAndSelectionRef.current = null;
    }
  }, [tooltipState, documentId, currentPage, onAnnotationCreate, onAnnotationUpdate]);

  // Handle tooltip cancel
  const handleTooltipCancel = useCallback(() => {
    setTooltipState(null);
    // Clean up the selection highlight
    if (hideTipAndSelectionRef.current) {
      hideTipAndSelectionRef.current();
      hideTipAndSelectionRef.current = null;
    }
  }, []);

  // Handle tooltip delete (in edit mode)
  const handleTooltipDelete = useCallback(async () => {
    if (tooltipState?.mode === 'edit' && tooltipState.annotation) {
      try {
        await onAnnotationDelete(tooltipState.annotation.id);
      } catch (error) {
        console.error('Error deleting annotation:', error);
      }
    }
    setTooltipState(null);
  }, [tooltipState, onAnnotationDelete]);

  // Handle new highlight from the library
  const handleAddHighlight = useCallback((highlight: NewHighlight) => {
    pauseForInteraction();
    // Show tooltip to add comment/tags
    setTooltipState({
      visible: true,
      mode: 'create',
      position: { top: 150, left: 150 },
      tempHighlight: highlight,
    });
  }, [pauseForInteraction]);

  // Render highlight popup
  const renderHighlightPopup = useCallback((highlight: IHighlight): React.ReactElement => {
    const annotation = annotations.find(a => a.id === highlight.id);

    return (
      <div style={{
        background: '#fff',
        padding: '8px',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        maxWidth: '200px'
      }}>
        <div style={{ fontWeight: 500, marginBottom: '4px' }}>
          {annotation?.comment || 'No comment'}
        </div>
        {annotation?.tags && annotation.tags.length > 0 && (
          <div style={{ fontSize: '12px', color: '#666' }}>
            Tags: {annotation.tags.join(', ')}
          </div>
        )}
      </div>
    );
  }, [annotations]);

  // Container styles
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  };

  const mainContentStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    position: 'relative', // anchor for absolute caption overlay
  };

  // On desktop the sidebar is a 360px column on the right; shrink the viewer
  // by that amount so PDF.js's `page-width` calculation accounts for the
  // hidden area instead of rendering pages behind the sidebar. The extra
  // 16px on each side gives the page some breathing room from the edges.
  // On mobile the sidebar overlays the whole screen — no shrink, no pad.
  const sidebarOpenDesktop = !isMobile && showSidebar;
  const SIDE_PAD = isMobile ? 0 : 16;
  const viewerContainerStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
    background: '#f5f5f5',
    paddingLeft: SIDE_PAD,
    paddingRight: SIDE_PAD,
    marginRight: sidebarOpenDesktop ? 360 : 0,
    transition: 'margin-right 0.25s ease',
  };

  return (
    <div style={containerStyle}>
      <style>{searchHighlightStyles}</style>
      {/* Toolbar */}
      <PDFToolbar
        currentPage={currentPage}
        totalPages={totalPages}
        zoom={displayZoom}
        effectiveZoomPercent={Math.round(effectiveScale * 100)}
        annotationCount={annotations.length}
        showAnnotations={showSidebar}
        exportLoading={exportLoading}
        hideExport={hideExport}
        exportPdfUrl={exportPdfUrl}
        onExportPdf={onExportPdf}
        onGenerateEstimate={onGenerateEstimate}
        estimateLoading={estimateLoading}
        enableAssistant={enableAssistant && !!onAssistantSend}
        assistantActive={showAssistant}
        onToggleAssistant={() => setShowAssistant((v) => !v)}
        documentTitle={documentTitle}
        onClose={onClose}
        isMobile={isMobile}
        onPageChange={handlePageChange}
        onZoomChange={handleZoomChange}
        onToggleAnnotations={() => setShowSidebar(!showSidebar)}
        onExportPage={handleExportPage}
      />

      {/* Main content area */}
      <div style={mainContentStyle}>
        {/* PDF Viewer */}
        <div style={viewerContainerStyle} ref={viewerContainerRef}>
          <PdfLoader
            url={pdfUrl}
            beforeLoad={<div style={{ padding: '40px', textAlign: 'center' }}><Spin size="large" tip="Loading PDF..." /></div>}
            onError={(error) => (
              <Alert
                type="error"
                message="Failed to load PDF"
                description={error.message}
                style={{ margin: '20px' }}
              />
            )}
          >
            {(pdfDocument) => {
              // Store reference to PDF document and update total pages
              if (pdfDocumentRef.current !== pdfDocument) {
                pdfDocumentRef.current = pdfDocument;
                // Use setTimeout to avoid setState during render
                setTimeout(() => setTotalPages(pdfDocument.numPages), 0);
              }

              return (
                <PdfHighlighter
                  pdfDocument={pdfDocument}
                  pdfScaleValue={pdfScaleValue}
                  enableAreaSelection={() => true} // Always enable area selection for testing
                  onScrollChange={() => {}}
                  scrollRef={(scrollTo) => {
                    scrollViewerToRef.current = scrollTo;
                  }}
                  onSelectionFinished={(
                    position,
                    content,
                    hideTipAndSelection,
                    transformSelection
                  ) => {
                    // Show a simple "Add highlight" button - our own custom tip
                    return (
                      <div
                        style={{
                          background: '#3b82f6',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          userSelect: 'none',
                        }}
                        onClick={() => {
                          // Transform selection first (creates the highlight visual)
                          transformSelection();
                          // Store the hide function to call later when annotation is saved/cancelled
                          hideTipAndSelectionRef.current = hideTipAndSelection;
                          // Create highlight data and show our custom dialog
                          const highlight: NewHighlight = {
                            position,
                            content,
                            comment: { text: '', emoji: '' },
                          };
                          handleAddHighlight(highlight);
                          // Don't call hideTipAndSelection here - wait until save/cancel
                        }}
                      >
                        Add Comment
                      </div>
                    );
                  }}
                  highlightTransform={(
                    highlight,
                    index,
                    setTip,
                    hideTip,
                    viewportToScaled,
                    screenshot,
                    isScrolledTo
                  ) => {
                    const annotation = annotations.find(a => a.id === highlight.id);
                    const color = annotation?.color || ANNOTATION_COLORS.yellow;

                    // Use the annotation type to determine how to render
                    const isTextHighlight = annotation?.type === 'text';

                    // Convert hex color to rgba with 20% opacity
                    const hexToRgba = (hex: string, alpha: number) => {
                      const r = parseInt(hex.slice(1, 3), 16);
                      const g = parseInt(hex.slice(3, 5), 16);
                      const b = parseInt(hex.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                    };

                    const component = isTextHighlight ? (
                      <div
                        onClick={() => {
                          if (annotation) {
                            handleEditAnnotation(annotation);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <Highlight
                          isScrolledTo={isScrolledTo}
                          position={highlight.position}
                          comment={highlight.comment}
                        />
                      </div>
                    ) : (
                      <div
                        onClick={() => {
                          if (annotation) {
                            handleEditAnnotation(annotation);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        className={`area-highlight-${highlight.id}`}
                      >
                        <AreaHighlight
                          isScrolledTo={isScrolledTo}
                          highlight={highlight}
                          onChange={() => {}}
                        />
                        <style>{`
                          .area-highlight-${highlight.id} .AreaHighlight {
                            background-color: ${hexToRgba(color, 0.2)} !important;
                            border: 2px solid ${color} !important;
                          }
                          .area-highlight-${highlight.id} .AreaHighlight__part {
                            background: ${hexToRgba(color, 0.2)} !important;
                          }
                          .area-highlight-${highlight.id} .AreaHighlight--scrolledTo .AreaHighlight__part {
                            background: ${hexToRgba(color, 0.3)} !important;
                          }
                        `}</style>
                      </div>
                    );

                    return (
                      <Popup
                        popupContent={renderHighlightPopup(highlight)}
                        onMouseOver={(popupContent) => setTip(highlight, () => popupContent)}
                        onMouseOut={hideTip}
                        key={index}
                      >
                        {component}
                      </Popup>
                    );
                  }}
                  highlights={highlights}
                />
              );
            }}
          </PdfLoader>
        </div>

        {/* Annotation Sidebar — slide-in overlay from the right edge of the
            viewer. Positioned absolutely so it covers the report instead of
            shrinking it. Toggled by the toolbar's Comments button. */}
        <div
          aria-hidden={!showSidebar}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: isMobile ? '100%' : '360px',
            maxWidth: '100%',
            transform: showSidebar ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.25s ease',
            zIndex: 28,
            boxShadow: showSidebar ? '-6px 0 18px rgba(0,0,0,0.18)' : 'none',
            pointerEvents: showSidebar ? 'auto' : 'none',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
          }}
        >
          <AnnotationSidebar
            annotations={annotations}
            currentPage={currentPage}
            onSelectAnnotation={handleSelectAnnotation}
            onEditAnnotation={handleEditAnnotation}
            onDeleteAnnotation={handleDeleteAnnotation}
            onAddGeneralNote={handleAddGeneralNote}
            enableWorkOrder={enableWorkOrder}
            onGenerateWorkOrder={onGenerateWorkOrder}
            workOrderLoading={workOrderLoading}
          />
        </div>

        {/* AI assistant — slide-in overlay mirroring the annotation sidebar.
            Toggled by the toolbar's "Ask AI" button. */}
        {enableAssistant && onAssistantSend && (
          <div
            aria-hidden={!showAssistant}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: isMobile ? '100%' : '380px',
              maxWidth: '100%',
              transform: showAssistant ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.25s ease',
              zIndex: 29,
              boxShadow: showAssistant ? '-6px 0 18px rgba(0,0,0,0.18)' : 'none',
              pointerEvents: showAssistant ? 'auto' : 'none',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
            }}
          >
            <ReportAssistant
              onSend={onAssistantSend}
              onClose={() => setShowAssistant(false)}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* First-load nudge toward the AI assistant. Anchored top-right under
            the "Ask AI" toolbar button, with an arrow pointing up at it. Fades
            in on load and out after ~3s. Non-interactive. */}
        {enableAssistant && onAssistantSend && (
          <div
            aria-hidden={!showAiHint}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 40,
              opacity: showAiHint ? 1 : 0,
              transition: 'opacity 0.45s ease',
              pointerEvents: 'none',
            }}
          >
            {/* arrow pointing up toward the Ask AI button */}
            <div
              style={{
                position: 'absolute',
                top: -5,
                right: isMobile ? 12 : 22,
                width: 10,
                height: 10,
                backgroundColor: 'rgba(17, 24, 39, 0.92)',
                transform: 'rotate(45deg)',
                borderRadius: 2,
              }}
            />
            <div
              style={{
                backgroundColor: 'rgba(17, 24, 39, 0.92)',
                color: '#fff',
                padding: '10px 14px',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                maxWidth: '75vw',
                boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
              }}
            >
              <RobotOutlined style={{ fontSize: 16, color: '#69b1ff', flexShrink: 0 }} />
              Ask our AI expert about this report
            </div>
          </div>
        )}

        {/* Narration captions overlay - absolutely positioned over the PDF area
            so it doesn't displace any layout. Sits above the bottom player bar. */}
        {sections && sections.length > 0 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 16,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 25,
              padding: '0 24px',
            }}
          >
            <NarrationCaptions text={captionText} visible={captionVisible} isMobile={isMobile} />
          </div>
        )}

        {/* Resume hint bubble — shown after narration is auto-paused due to
            user interaction (zoom, scroll, opening an annotation). Click
            resumes playback so the user can keep listening once they're done
            commenting. */}
        {sections && sections.length > 0 && showResumeHint && !isNarrating && (
          <div
            onClick={() => {
              setShowResumeHint(false);
              narrationPlayerRef.current?.play();
            }}
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: 16,
              zIndex: 26,
              backgroundColor: 'rgba(24, 144, 255, 0.96)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 500,
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              cursor: 'pointer',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ fontSize: 16 }}>▶</span>
            Paused — click play to continue
          </div>
        )}
      </div>

      {/* Narration player - sticky bar below the main content. The PDF
          search box is composed into the same bar (right-side on desktop,
          stacked on mobile via flexWrap). */}
      {sections && sections.length > 0 ? (
        <NarrationPlayer
          ref={narrationPlayerRef}
          sections={sections}
          onPageChange={handleNarrationPageChange}
          onCaptionUpdate={handleCaptionUpdate}
          isMobile={isMobile}
          rightSlot={
            <PDFSearchBox
              pdfDocumentRef={pdfDocumentRef}
              isMobile={isMobile}
              variant="dark"
            />
          }
        />
      ) : (
        // Slim sticky bar with just the search box when there's no narration.
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 30,
            backgroundColor: '#fff',
            borderTop: '1px solid #e5e7eb',
            padding: isMobile ? '6px 10px' : '6px 16px',
            display: 'flex',
            justifyContent: 'flex-end',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <PDFSearchBox
            pdfDocumentRef={pdfDocumentRef}
            isMobile={isMobile}
            variant="light"
          />
        </div>
      )}

      {/* Annotation Tooltip for create/edit */}
      {tooltipState?.visible && (
        <AnnotationTooltip
          position={tooltipState.position}
          initialComment={tooltipState.annotation?.comment || ''}
          initialTags={tooltipState.annotation?.tags || []}
          initialColor={tooltipState.annotation?.color || ANNOTATION_COLORS.yellow}
          mode={tooltipState.mode}
          onSave={handleTooltipSave}
          onCancel={handleTooltipCancel}
          onDelete={tooltipState.mode === 'edit' ? handleTooltipDelete : undefined}
        />
      )}
    </div>
  );
};
